import React, { useEffect, useMemo } from "react";
import { animated, Transition } from "react-spring";
import { _CartItem } from "@organisms/CartList/Item/CartItem.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
// import { useProducts } from "@danishagro/shared/src/gtm/useProducts";
// import { useCart } from "@danishagro/shared/src/contexts/cart/cart.context";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import {
    DA_DynamicOverlayWrapper,
    useDynamicOverlay,
} from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { DA_ButtonLink } from "@danishagro/shared/src/components/atoms/ButtonLink/ButtonLink.component";
import { useProducts } from "@danishagro/shared/src/gtm/useProducts";
import { DA_ErrorBoundary } from "@danishagro/shared/src/components/molecules/ErrorBoundary/ErrorBoundary.component";
import { DA_CartListProps } from "./CartList.props";
import { useMarkAll } from "./hooks/useMarkAll.hook";
// eslint-disable-next-line css-modules/no-unused-class
import S from "./CartList.module.scss";
// import { useBasket } from "@danishagro/shared/src/contexts/basket/basket.context";

export const DA_CartList = ({ ...props }: DA_CartListProps) => {
    const { getDictionaryString } = useTranslations();
    const { isMobileOrTablet } = useScreen();
    const { deletedItems, deleteItem, showCheckboxes, setShowCheckboxes } = useMarkAll();

    // Check if any product is selected for deletion
    const hasCheckedProduct = deletedItems.length > 0;

    // State to keep track of deleted item IDs
    // const { showModal, closeModal } = useModal();

    const { closeOverlay, showOverlay } = useDynamicOverlay();

    const texts = useMemo(
        () => ({
            name: getDictionaryString("cart.cartList.name", { uppercaseFirst: true }),
            date: getDictionaryString("cart.cartList.latestDeliveryDate", {
                uppercaseFirst: true,
            }),
            amount: getDictionaryString("cart.cartList.amount", { uppercaseFirst: true }),
            comment: getDictionaryString("cart.cartList.comment", { uppercaseFirst: true }),
            price: getDictionaryString("cart.cartList.price", { uppercaseFirst: true }),
        }),
        [getDictionaryString]
    );

    // GTM Tracking
    // TODO: Wait for priceService, and add price
    // const transformedItems = useMemo(() => {
    //     return props.items.map((item) => {
    //         // Extract price for each line
    //         // const priceInfo = priceData?.lines.find((line) => line?.orderLineId === item?.id);
    //         const priceInfo = priceData?.lines.find((line) => line?.orderLineId);

    //         // Extract the category from the URL
    //         const category = item?.url?.split("/").slice(0, -1).join("_").replace(/^_/, "");

    //         return {
    //             productName: item.productName,
    //             productNumber: item.productNumber,
    //             price: priceInfo ? priceInfo.unitPrice.priceWithoutVat : 0,
    //             currency: "DKK",
    //             quantity: item.quantity,
    //             category,
    //             // ... other properties
    //         };
    //     });
    // }, [props.items, priceData]);

    // Helper functions
    const getCategoryFromUrl = (url: string = "") => {
        if (!url) return "";
        const segments = url.split("/").filter(Boolean); // removes leading/trailing empty
        return segments.slice(0, -1).join("_"); // remove last (product), join with _
    };

    const getListNameFromUrl = (url: string = "") => {
        if (!url) return "";
        const segments = url?.split("/").filter(Boolean);
        const lastCategory = segments.length > 1 ? segments[segments.length - 2] : "";
        return capitalizeFirst(lastCategory);
    };

    const capitalizeFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

    const transformedItems = useMemo(
        () =>
            props.items.map((item) => {
                // Defensive price extraction
                let price = 0;
                if (
                    typeof item.price === "object" &&
                    item.price !== null &&
                    typeof item.price.price === "number"
                ) {
                    price = item.price.price;
                } else if (typeof item.price === "number") {
                    price = item.price;
                }

                const category = getCategoryFromUrl(item?.url);
                const itemListName = getListNameFromUrl(item.url);

                return {
                    productName: item.productName,
                    productNumber: item.productNumber,
                    price, // always a number now!
                    currency: "DKK",
                    quantity: item.quantity,
                    category: category,
                    listName: itemListName,
                    brand: item?.brandName || "",
                };
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.items]
    );

    const trackProducts = useProducts(transformedItems);

    useEffect(() => {
        if (transformedItems.length > 0) {
            trackProducts("view_cart");
        }
    }, [transformedItems, trackProducts]);

    const onQuickSelectChanged = () => {
        setShowCheckboxes(!showCheckboxes);
    };

    const activateMarkAllBottomSheet = () => {
        showOverlay(
            {
                id: "activateMarkAll",
                title: getDictionaryString("activateMarkAll"),
                closeButtonToBack: true,
                content: (
                    <DA_DynamicOverlayWrapper
                        content={
                            <DA_ButtonLink
                                onClick={() => {
                                    closeOverlay();
                                    onQuickSelectChanged();
                                }}
                                className={S.quickSelectLink}
                                title={getDictionaryString("MarkAllAndRemove")}
                            />
                        }
                        footer={null}
                    />
                ),
            },
            {
                bottomSheet: {
                    snapPoints: [50],
                },
            }
        );
    };

    // const deleteItemsBottomSheet = useCallback(() => {
    //     showOverlay(
    //         {
    //             id: "deleteItemsOverlay",
    //             title: getDictionaryString("deleteItemsOverlayTitle"),
    //             closeButtonToBack: true,
    //             content: (
    //                 <DA_DynamicOverlayWrapper
    //                     content={
    //                         <DA_DeleteItemsOverlay
    //                             onClick={() => {
    //                                 confirmDeleteItems(deletedItems.map((i) => i.id));
    //                                 closeOverlay();
    //                             }}
    //                             onClose={closeOverlay}
    //                         />
    //                     }
    //                     footer={null}
    //                 />
    //             ),
    //         },
    //         {
    //             bottomSheet: {
    //                 snapPoints: ({ minHeight }) => [minHeight],
    //             },
    //         }
    //     );
    // }, [closeOverlay, confirmDeleteItems, deletedItems, getDictionaryString, showOverlay]);

    // const deleteItemsModal = useCallback(() => {
    //     showModal(
    //         <DA_DeleteItemsOverlay
    //             onClick={() => confirmDeleteItems(deletedItems.map((i) => i.id))}
    //             onClose={closeModal}
    //         />,
    //         {
    //             size: ModalSize.XS,
    //         }
    //     );
    // }, [closeModal, confirmDeleteItems, deletedItems, showModal]);

    // const MarkAllControlComponent: React.FC<{
    //     items: _CartItemProps[];
    //     disableControls?: boolean;
    // }> = ({ items, disableControls }) => {
    //     return !disableControls ? (
    //         <>
    //             {isMobileOrTablet && (
    //                 <DA_BasicLink
    //                     className={S.buttonCancel}
    //                     href="#"
    //                     onClick={() => cancelSelection()}
    //                 >
    //                     {getDictionaryString("quickSelectCancel")}
    //                 </DA_BasicLink>
    //             )}
    //             <DA_Button
    //                 onClick={() => markAllItems(items)}
    //                 title={getDictionaryString("MarkAllButton")}
    //                 isGhost
    //             />
    //             <DA_Button
    //                 onClick={() => {
    //                     isMobileOrTablet ? deleteItemsBottomSheet() : deleteItemsModal();
    //                 }}
    //                 title={getDictionaryString(
    //                     isMobileOrTablet ? "MarkAndRemoveButton" : "MarkAndRemoveButtonDesktop"
    //                 )}
    //                 icon={DA_IconNames.Trash}
    //                 disabled={deletedItems.length === 0}
    //                 iconRight
    //                 color={DA_ButtonColor.Alert}
    //             />
    //         </>
    //     ) : null;
    // };

    useEffect(() => {
        // TODO: Disable mark all for now
        setShowCheckboxes(false);
    }, [setShowCheckboxes]);

    return (
        <>
            {/* {showCheckboxes && isMobileOrTablet && (
                <div className={cn(S.controlsWrapper, S.mobile)}>
                    <MarkAllControlComponent
                        items={props.items}
                        disableControls={props.isReceiptPage}
                    />
                </div>
            )} */}
            {/* {!showCheckboxes && isMobileOrTablet && (
                <button
                    className={cn(
                        S.quickSelectTogggleButton,
                        isMobileOrTablet && S.mobilePositionButton
                    )}
                    onClick={() => activateMarkAllBottomSheet()}
                >
                    <DA_Icon className={S.quickSelectTogggleIcon} name={DA_IconNames.Ellipsis} />
                </button>
            )} */}

            <section className={cn(S.cartList, props.noShadow || S.withShadow, props.className)}>
                <div
                    className={cn(
                        S.quickSelectWrapper,
                        isMobileOrTablet && S.mobileOrTablet,
                        showCheckboxes && isMobileOrTablet && S.mobilePosition
                    )}
                >
                    {/* {!isMobileOrTablet ? (
                        <div className={S.controlsWrapper}>
                            <MarkAllControlComponent
                                items={props.items}
                                disableControls={props.isReceiptPage}
                            />
                        </div>
                    ) : null} */}
                </div>

                {/** Header */}
                {!props.readOnly ? (
                    <div className={S.header} aria-hidden={true}>
                        <div className={S.headerItem}>{texts.name}</div>
                        <div className={S.headerItem}>{texts.amount}</div>
                        <div className={S.headerItem}>{texts.price}</div>
                    </div>
                ) : null}
                <Transition
                    items={props.items}
                    from={{ opacity: 1 }}
                    leave={{ opacity: 0 }}
                    keys={(item) => item.id}
                >
                    {(style, item) => (
                        <animated.div className={S.item} style={style}>
                            <DA_ErrorBoundary featureName="cartItem" featureVersion="1.0">
                                <_CartItem
                                    key={item.id}
                                    {...item}
                                    linePrice={item.price}
                                    hideDeliveryOptions={props.hideDeliveryOptions}
                                    hideDriverMessage={props.hideDriverMessage}
                                    hideDriverMessageButton={props.hideDriverMessageButton}
                                    farmInTimeReferenceId={props.farmInTimeReferenceId}
                                    readOnly={props.readOnly}
                                    isReceiptPage={props.isReceiptPage}
                                    showCheckboxes={showCheckboxes}
                                    checked={deletedItems.some((i) => i.id === item.id)}
                                    onCheckboxChange={() => deleteItem(item)}
                                    disableDeleteButtons={hasCheckedProduct}
                                />
                            </DA_ErrorBoundary>
                        </animated.div>
                    )}
                </Transition>
            </section>
        </>
    );
};
